import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Shield, X, Check, CreditCard, Clock, Heart, Zap, Sparkles } from 'lucide-react';

const PackagePopup = ({ pkg, onClose }) => {
  if (!pkg) return null;
  
  const bgColor = pkg.popular ? 'bg-gradient-to-b from-[#f87171] to-[#e05858]' : 
                  pkg.id === 'premium' ? 'bg-[#1a365d]' : 'bg-slate-100';
  const textColor = pkg.id === 'osnovni' ? 'text-[#1a365d]' : 'text-white';
  
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto" onClick={onClose}>
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className={`${bgColor} p-6 relative`}>
          {pkg.popular && (
            <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">
              Najbolj priljubljen
            </div>
          )}
          <button onClick={onClose} className="absolute top-3 left-3 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30">
            <X className={`w-4 h-4 ${textColor}`} />
          </button>
          <div className="text-center pt-4">
            <div className="text-5xl mb-3">{pkg.emoji}</div>
            <h3 className={`text-2xl font-bold ${textColor} mb-1`}>{pkg.name}</h3>
            <p className={`text-sm ${textColor} opacity-80`}>{pkg.frequency}</p>
            <div className={`text-4xl font-bold ${textColor} mt-4`}>
              {pkg.price}€<span className="text-lg opacity-70">/mesec</span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <p className="text-slate-600 mb-6 text-center">{pkg.description}</p>
          
          <div className="space-y-3 mb-6">
            {pkg.benefits.split('|').map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-[#f87171]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-[#f87171]" />
                </div>
                <span className="text-sm text-slate-700">{b}</span>
              </div>
            ))}
          </div>
          
          {/* Gym24 partnership removed (Feb 2026 pricing update) — the
              fitness card is no longer part of the Premium package. */}
          
          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-[#f87171]" />
              <span className="font-semibold text-sm text-[#1a365d]">Za koga je primerno?</span>
            </div>
            <p className="text-sm text-slate-600">{pkg.idealFor}</p>
          </div>
          
          <div className="space-y-3">
            <Link to="/mesecni-program" className="flex items-center justify-center gap-2 bg-[#f87171] hover:bg-[#e05858] text-white px-6 py-4 rounded-full font-semibold transition-all w-full">
              <CreditCard className="w-5 h-5" />
              Naroči se zdaj
            </Link>
            <Link to="/mesecni-program" className="flex items-center justify-center gap-2 text-[#1a365d] hover:text-[#f87171] px-6 py-3 font-medium transition-colors w-full" onClick={onClose}>
              Več informacij
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

const PackageCard = ({ pkg, onClick, isMobile }) => {
  const baseClass = isMobile 
    ? `flex-shrink-0 w-28 rounded-2xl p-4 text-center transition-all`
    : `w-36 lg:w-40 rounded-2xl p-5 text-center transition-all duration-300 hover:scale-110 hover:-translate-y-2 cursor-pointer relative`;
  
  const colorClass = pkg.popular 
    ? (isMobile ? 'bg-[#f87171] shadow-lg' : 'bg-[#f87171] shadow-xl scale-105 -translate-y-1')
    : 'bg-white shadow-md border border-slate-100 hover:shadow-lg';
    
  return (
    <button onClick={onClick} className={`${baseClass} ${colorClass}`} data-testid={`pkg-${isMobile ? 'mobile' : 'desktop'}-${pkg.id}`}>
      {pkg.popular && !isMobile && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#1a365d] text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
          TOP
        </div>
      )}
      <div className={isMobile ? "text-2xl mb-2" : "text-3xl mb-2"}>{pkg.emoji}</div>
      <h3 className={`font-bold mb-1 ${isMobile ? 'text-sm' : 'text-base'} ${pkg.popular ? 'text-white' : 'text-[#1a365d]'}`}>{pkg.name}</h3>
      <p className={`text-xs mb-2 ${pkg.popular ? 'text-white/80' : 'text-slate-500'}`}>{pkg.frequency}</p>
      <div className={`font-bold ${isMobile ? 'text-lg' : 'text-2xl mb-2'} ${pkg.popular ? 'text-white' : 'text-[#f87171]'}`}>{pkg.price}€</div>
      {!isMobile && <div className={`text-xs ${pkg.popular ? 'text-white/70' : 'text-slate-400'}`}>Klikni za več →</div>}
    </button>
  );
};

const MonthlyProgramBanner = () => {
  const [selectedPkg, setSelectedPkg] = useState(null);
  
  const pkgOsnovni = { id: 'osnovni', name: 'Osnovni', emoji: '🥉', price: 70, frequency: '1x mesečno', popular: false, description: 'Idealno za vzdrževanje dobrega počutja.', benefits: 'Ena obravnava mesečno (45 min)|Izbira katerekoli storitve|Prednostna rezervacija|Minimalno obdobje: 3 mesece|Neporabljene obravnave se ne prenašajo v naslednji mesec|Terapije so osebne in neprenosne', idealFor: 'Za vse, ki želijo z mesečnim servisom telesa ohranjati dobro počutje in stanje brez bolečin.' };
  const pkgAktivni = { id: 'aktivni', name: 'Aktivni', emoji: '🥈', price: 130, frequency: '2x mesečno', popular: true, description: 'Najboljše razmerje med ceno in koristjo.', benefits: 'Dve obravnavi mesečno (2x 45 min)|Kombinacija storitev|Prednostna rezervacija|10% popust na redne cene storitev|Minimalno obdobje: 3 mesece|Neporabljene obravnave se ne prenašajo v naslednji mesec|Terapije so osebne in neprenosne', idealFor: 'Za športnike in aktivne.' };
  const pkgPremium = { id: 'premium', name: 'Premium', emoji: '🥇', price: 180, frequency: '3x mesečno', popular: false, description: 'Za maksimalno skrb in dodatne ugodnosti.', benefits: 'Tri obravnave mesečno|Vse storitve vključene|Najvišja prednost rezervacij|Minimalno obdobje: 3 mesece|Neporabljene obravnave se ne prenašajo v naslednji mesec|Terapije so osebne in neprenosne', idealFor: 'Za rehabilitacijo in profesionalce.' };
  
  const packages = [pkgOsnovni, pkgAktivni, pkgPremium];
  
  return (
    <>
      <section className="py-8 md:py-12 relative overflow-hidden" data-testid="monthly-program-banner">
        {/* Light background with soft coral accents */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#fff8f8] to-[#ffefef]" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#f87171]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#f87171]/5 rounded-full blur-[80px]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-[#f87171]/10 text-[#f87171] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Heart className="w-4 h-4" />
                Mesečni program
              </div>
              <h2 className="text-2xl font-bold text-[#1a365d] mb-2">Telo vrača, če ga vzdržuješ.</h2>
            </div>
            
            {/* Saša's image and quote - Mobile */}
            <div className="flex items-center gap-4 mb-6">
              <img loading="lazy" decoding="async" 
                src="/images/physiolab-mesecni-program-fizioterapija.png"
                alt="Saša Panič"
                className="w-32 h-auto flex-shrink-0"
                style={{ filter: 'drop-shadow(0 10px 20px rgba(248,113,113,0.15))' }}
              />
              <div className="flex-1">
                <p className="text-slate-600 text-sm leading-relaxed">
                  Preventiva je najboljša terapija. Z rednimi obiski investirate v svoje zdravje.
                </p>
                <p className="text-[#f87171] text-xs font-semibold mt-2">— Saša Panič, dipl. fiziot.</p>
              </div>
            </div>
            
            {/* Packages - Mobile */}
            <div className="flex gap-3 overflow-x-auto pb-4 -mx-4 px-4 mb-4">
              <PackageCard pkg={pkgOsnovni} onClick={() => setSelectedPkg(pkgOsnovni)} isMobile />
              <PackageCard pkg={pkgAktivni} onClick={() => setSelectedPkg(pkgAktivni)} isMobile />
              <PackageCard pkg={pkgPremium} onClick={() => setSelectedPkg(pkgPremium)} isMobile />
            </div>
            
            <Link to="/mesecni-program" className="flex items-center justify-center gap-2 bg-[#1a365d] text-white px-6 py-3.5 rounded-full font-semibold w-full shadow-lg" data-testid="monthly-cta-mobile">
              Več o programu
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden md:block">
            <div className="flex items-center gap-8 lg:gap-12">
              
              {/* Left side - Saša */}
              <div className="flex-shrink-0 relative">
                {/* Decorative coral glow */}
                <div className="absolute -inset-6 bg-gradient-to-br from-[#f87171]/20 via-[#f87171]/10 to-transparent rounded-full blur-2xl" />
                
                <img loading="lazy" decoding="async" 
                  src="/images/physiolab-mesecni-program-fizioterapija.png"
                  alt="Saša Panič - Fizioterapevtka"
                  className="w-52 lg:w-60 h-auto relative z-10"
                  style={{ filter: 'drop-shadow(0 15px 30px rgba(248,113,113,0.15))' }}
                />
              </div>
              
              {/* Middle - Content */}
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-2 bg-[#f87171]/10 text-[#f87171] px-4 py-2 rounded-full text-sm font-semibold mb-5">
                  <Sparkles className="w-4 h-4" />
                  Mesečni program
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-bold text-[#1a365d] mb-3">
                  Telo vrača, <span className="text-[#f87171]">če ga vzdržuješ.</span>
                </h2>
                
                <p className="text-slate-600 text-base lg:text-lg mb-6 max-w-lg">
                  Preventiva je najboljša terapija. Z rednimi mesečnimi obiski investirate v svoje zdravje.
                </p>
                
                <div className="flex flex-wrap gap-x-5 gap-y-3 mb-6">
                  <div className="flex items-center gap-2 text-slate-700">
                    <div className="w-9 h-9 bg-[#f87171]/10 rounded-full flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-[#f87171]" />
                    </div>
                    <span className="text-sm">Redni termini</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <div className="w-9 h-9 bg-[#f87171]/10 rounded-full flex items-center justify-center">
                      <Heart className="w-4 h-4 text-[#f87171]" />
                    </div>
                    <span className="text-sm">Osebna skrb</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <div className="w-9 h-9 bg-[#f87171]/10 rounded-full flex items-center justify-center">
                      <Zap className="w-4 h-4 text-[#f87171]" />
                    </div>
                    <span className="text-sm">Do 20% prihranka</span>
                  </div>
                </div>
                
                <Link to="/mesecni-program" className="inline-flex items-center gap-2 bg-[#1a365d] hover:bg-[#1a365d]/90 text-white px-7 py-3.5 rounded-full font-semibold transition-all hover:scale-105 shadow-lg" data-testid="monthly-cta-desktop">
                  Odkrijte pakete
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              
              {/* Right side - Package cards */}
              <div className="flex-shrink-0 flex gap-3 lg:gap-4">
                <PackageCard pkg={pkgOsnovni} onClick={() => setSelectedPkg(pkgOsnovni)} />
                <PackageCard pkg={pkgAktivni} onClick={() => setSelectedPkg(pkgAktivni)} />
                <PackageCard pkg={pkgPremium} onClick={() => setSelectedPkg(pkgPremium)} />
              </div>
            </div>
          </div>
          
        </div>
      </section>
      
      {selectedPkg && <PackagePopup pkg={selectedPkg} onClose={() => setSelectedPkg(null)} />}
    </>
  );
};

export default MonthlyProgramBanner;
